import { useCallback } from 'react';
import { TimekeeperService } from 'services/TimekeeperService';
import { ScheduleDesviationBase } from 'repository/timekeeper/@types/ScheduleDesviationRuleModel';
import { SnackbarMessage } from 'notistack';

interface AlertMessage {
    title: string;
    message: SnackbarMessage;
    severity: 'error' | 'success' | 'warning' | 'info';
}

type SetLoading = (loading: boolean) => void;
type Callback = () => void;
type HandlerAlert = (message: AlertMessage) => void;

const timekeeperService = new TimekeeperService();

export const useScheduleDesviationActions = () => {
    const addScheduleDesviation = useCallback(
        async ({
            setLoading,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            body: ScheduleDesviationBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.schedule_desviation.createScheduleDesviation(body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const updateScheduleDesviation = useCallback(
        async ({
            setLoading,
            scheduleDesviationId,
            body,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            scheduleDesviationId: number;
            body: ScheduleDesviationBase;
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await timekeeperService.schedule_desviation.updateScheduleDesviation(scheduleDesviationId, body);
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    const deleteScheduleDesviations = useCallback(
        async ({
            setLoading,
            scheduleDesviationIds,
            closing,
            reloading,
            messageError,
            messageSuccess,
            handlerAlert,
        }: {
            setLoading: SetLoading;
            scheduleDesviationIds: number[];
            closing: Callback;
            reloading: Callback;
            messageError: AlertMessage;
            messageSuccess: AlertMessage;
            handlerAlert: HandlerAlert;
        }) => {
            setLoading(true);

            try {
                await Promise.all(scheduleDesviationIds.map((scheduleDesviationId) => timekeeperService.schedule_desviation.deleteScheduleDesviation(scheduleDesviationId)));
                reloading();
                closing();
                handlerAlert(messageSuccess);
            } catch (error: any) {
                console.error(error?.message || error);
                handlerAlert(messageError);
            } finally {
                setLoading(false);
            }
        },
        [],
    );

    return {
        addScheduleDesviation,
        updateScheduleDesviation,
        deleteScheduleDesviations,
    };
};
